import { useTracking } from 'react-components/tracking';
import { DatadogTrackingProps } from 'toolkit/environment/environment/userMonitoring.util';

/**
 * Generates form field tracking props, masking sensitive fields by default.
 * @param trackingName - Stable field tracking name.
 * @param sensitive - Whether the field may contain PHI. Defaults to true.
 * @returns Datadog attributes for the field root, or an empty object.
 */
export const useFormTracking = (
  trackingName?: string,
  sensitive: boolean = true
): DatadogTrackingProps => {
  const { generateTrackingProps } = useTracking();

  if (!trackingName) {
    return {};
  }

  return generateTrackingProps(
    trackingName,
    sensitive ? { privacyLevel: 'mask' } : undefined
  );
};

/**
 * Generates option tracking props in form fields.
 * @param trackingName - The name of the tracking context for this field.
 * @param sensitive - Whether the options should be tracked for privacy.
 * @param options - The options array to generate tracking for.
 * @returns Function that generates tracking props for individual options.
 */
export const useFormOptionTracking = (
  trackingName?: string,
  sensitive: boolean = true,
  options: unknown[] = []
) => {
  const { generateTrackingProps } = useTracking();

  const generateOptionTracking = (value: unknown, _label: string) => {
    if (!trackingName) {
      return {};
    }

    if (sensitive) {
      // For sensitive data, use index-based tracking for privacy
      const optionIndex = options.findIndex(option =>
        typeof option === 'object' && option !== null && 'value' in option
          ? option.value === value
          : option === value
      );

      if (optionIndex === -1) {
        return {};
      }

      return generateTrackingProps(`${trackingName}-option-${optionIndex}`);
    }

    // For non-sensitive data, use the actual value
    return generateTrackingProps(`${trackingName}-option-${value}`);
  };

  return { generateOptionTracking };
};
